import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { dropStaleChoiceSelections, deriveComputedValueKey, resolveDatabasePrepopulation } from "../../utils";
import { useCallback, useMemo, useRef, useState } from "react";
import { FormTypesWithValues, TellescopeFieldResponse } from "../../types";
import {
  AnswerForType,
  DatabaseRecord,
  Enduser,
  FormResponseValue,
  FormResponseValueAnswer,
} from "@tellescope/types-models";
import { usePendingFileUploads } from "./use-pending-file-uploads";
import { useSubmitPhase } from "./use-submit-phase";
import { resolveFieldValidity } from "./resolve-field-validity";
import { useResolveCurrentResponses } from "./use-resolve-current-responses";
import { isRepeatMismatch } from "./resolve-repeat-mismatch";
import { useFieldValidation } from "./use-field-validation";
import { useFormDataQueries } from "./use-form-data-queries";
import { useTouchedFields } from "./use-touched-fields";
import { useVisibleFields } from "./use-visible-fields";
import { useSaveResponse } from "./use-save-response";
import { isFormComplete } from "./is-form-complete";

export type LocalTellescopeFormResponseMap = Record<string, TellescopeFieldResponse>;

export interface UseTellescopeFormCoreArgs {
  formId: string;
  formTitle?: string;
  responseId?: string;
  enduserId: string;
  parentResponseId?: string;
  rootResponseId?: string;
  existingResponses?: FormResponseValue[];
  accessCode?: string;
  enduser?: TellescopeModelFromName<"endusers"> | Enduser;
  urlLogicValue?: string;
  publicIdentifier?: string;
}

/**
 * Internal hook containing shared logic for both single-page and enduser form flows.
 * Not intended for direct consumption — use `useTellescopeSinglePageForm` or `useTellescopeEnduserForm`.
 */
export const useTellescopeFormCore = ({
  formId,
  formTitle: providedFormTitle,
  responseId,
  enduserId,
  parentResponseId,
  rootResponseId,
  existingResponses,
  accessCode,
  enduser,
  urlLogicValue: providedUrlLogicValue,
  publicIdentifier,
}: UseTellescopeFormCoreArgs) => {
  const resolveCurrentResponses = useResolveCurrentResponses({ publicIdentifier });

  const {
    fields,
    response,
    formTitle,
    responsesMap,
    enduserForPrepopulation,
    formQuery,
    formFieldsQuery,
    responseQuery,
    enduserResponseInfoQuery,
    enduserQuery,
  } = useFormDataQueries({ formId, providedFormTitle, responseId, enduserId, accessCode, enduser });

  // The write target of setFieldValue and the top of the currentResponses precedence chain.
  const [unsavedResponses, setUnsavedResponses] = useState<LocalTellescopeFormResponseMap>({});
  const validateFieldRef = useRef<(args: { fieldId: string }) => Promise<string | null>>(() => Promise.resolve(null));
  const {
    touchedFields,
    setTouchedFields,
    repeatInputs,
    setRepeatValue,
    repeatTouchedFields,
    handleFieldBlur,
    handleRepeatBlur,
  } = useTouchedFields({ validateFieldRef });

  const currentResponses = useMemo(
    () =>
      resolveCurrentResponses({
        fieldsArr: fields?.fieldsArr,
        unsavedResponses,
        responsesMap,
        existingResponses,
        enduserForPrepopulation,
      }),
    [
      fields?.fieldsArr,
      unsavedResponses,
      responsesMap,
      existingResponses,
      enduserForPrepopulation,
      resolveCurrentResponses,
    ]
  );

  const currentResponsesRef = useRef(currentResponses);
  currentResponsesRef.current = currentResponses;

  const { pendingFiles, setPendingFilesForField, uploadPendingFiles, uploadProgress, setUploadProgress } =
    usePendingFileUploads({
      fieldsMap: fields?.fieldsMap,
      currentResponses,
      enduserId,
      setUnsavedResponses,
    });

  const uncommittedValuesRef = useRef<Record<string, FormResponseValueAnswer["value"]>>({});
  uncommittedValuesRef.current = Object.entries(uncommittedValuesRef.current).reduce<
    Record<string, FormResponseValueAnswer["value"]>
  >((acc, [fieldId, value]) => {
    const committed = currentResponses[fieldId]?.answer.value;
    if (JSON.stringify(committed) !== JSON.stringify(value)) acc[fieldId] = value;
    return acc;
  }, {});

  const repeatMismatchFieldIds = useMemo(
    () =>
      Object.values(currentResponses).reduce<Set<string>>((acc, response) => {
        const field = fields?.fieldsMap[response.fieldId];
        if (!field) return acc;
        if (
          isRepeatMismatch({ field, value: response.answer?.value, confirmation: repeatInputs[response.fieldId] ?? "" })
        )
          acc.add(response.fieldId);
        return acc;
      }, new Set()),
    [currentResponses, fields?.fieldsMap, repeatInputs]
  );

  const { logicOptions, subFieldIds, isSubFieldHidden, visibleFields } = useVisibleFields({
    fieldsArr: fields?.fieldsArr,
    fieldsMap: fields?.fieldsMap,
    currentResponses,
    urlLogicValue: providedUrlLogicValue,
    enduser: enduserQuery.data,
  });

  const { fieldErrors, setFieldErrors, validateField, validateAllFields } = useFieldValidation({
    fieldsMap: fields?.fieldsMap,
    fieldsArr: fields?.fieldsArr,
    currentResponses,
    currentResponsesRef,
    uncommittedValuesRef,
    pendingFiles,
    repeatInputs,
    isSubFieldHidden,
    setTouchedFields,
  });

  validateFieldRef.current = validateField;

  const setFieldValue = useCallback(
    <TField extends FormTypesWithValues>(fieldId: string, value: AnswerForType[TField]) => {
      uncommittedValuesRef.current[fieldId] = value;
      setUnsavedResponses((prev) => {
        const field = fields?.fieldsMap[fieldId];
        const originalResponse = prev[fieldId] ??
          currentResponsesRef.current[fieldId] ?? {
            fieldId,
            fieldTitle: field?.title ?? "",
            answer: {
              type: field?.type,
              value: undefined,
            },
          };
        if (
          value === undefined ||
          originalResponse?.answer.value === undefined ||
          typeof value === typeof originalResponse?.answer.value
        ) {
          const newAnswer = {
            ...originalResponse?.answer,
            value,
          } as FormResponseValueAnswer;
          const newResponse: FormResponseValue = {
            ...originalResponse,
            answer: newAnswer,
            computedValueKey: deriveComputedValueKey(field?.intakeField, newAnswer),
          };
          const next = {
            ...prev,
            [fieldId]: newResponse,
          };
          return dropStaleChoiceSelections({
            responses: next,
            fieldsArr: fields?.fieldsArr ?? [],
            changedFieldId: fieldId,
            logicOptions,
          });
        }
        return prev;
      });
    },
    [fields?.fieldsMap, fields?.fieldsArr, logicOptions]
  );

  const applyDatabasePrepopulation = useCallback(
    (records: DatabaseRecord[]) => {
      const prepopulated = resolveDatabasePrepopulation({
        fieldsArr: fields?.fieldsArr ?? [],
        records,
        currentResponses: currentResponsesRef.current,
      });
      if (!Object.keys(prepopulated).length) return;
      setUnsavedResponses((prev) => ({ ...prev, ...prepopulated }));
    },
    [fields?.fieldsArr]
  );

  const [customerId, setCustomerId] = useState<string | undefined>(undefined);

  const isFieldValueValid = useCallback(
    (fieldId: string): boolean => {
      const field = fields?.fieldsMap[fieldId];
      if (!field) return true;

      const { valid, error } = resolveFieldValidity({
        field,
        fieldsMap: fields?.fieldsMap,
        currentResponses,
        pendingFiles,
        isSubFieldHidden,
      });

      if (error !== undefined) setFieldErrors((prev) => ({ ...prev, [field.id]: error }));

      return valid;
    },
    [fields?.fieldsMap, currentResponses, isSubFieldHidden, pendingFiles]
  );

  const { prepareFormResponseMutation, preparedResponse, submitFormMutation, saveFieldResponseMutation, saveResponse } =
    useSaveResponse({
      formId,
      enduserId,
      responseId,
      parentResponseId,
      rootResponseId,
      formTitle,
      response,
      currentResponses,
    });

  const { submitPhase, runSubmit } = useSubmitPhase({ uploadPendingFiles, setUploadProgress });

  const isComplete = useMemo(
    () =>
      isFormComplete({
        visibleFields,
        fieldsMap: fields?.fieldsMap,
        currentResponses,
        fieldErrors,
        logicOptions,
      }),
    [visibleFields, fields?.fieldsMap, currentResponses, fieldErrors, logicOptions]
  );

  return {
    formQuery,
    formFieldsQuery,
    responseQuery,
    enduser: accessCode ? enduser : enduserQuery.data,
    enduserResponseInfoQuery,
    formTitle,
    prepareFormResponseMutation,
    preparedResponse,
    submitFormMutation,
    saveFieldResponseMutation,
    currentResponses,
    responsesMap,
    fieldErrors,
    touchedFields,
    logicOptions,
    visibleFields,
    isComplete,
    validateField,
    validateAllFields,
    setFieldValue,
    handleFieldBlur,
    saveResponse,
    fields,
    subFieldIds,
    isSubFieldHidden,
    repeatInputs,
    repeatTouchedFields,
    setRepeatValue,
    handleRepeatBlur,
    repeatMismatchFieldIds,
    isFieldValueValid,
    pendingFiles,
    setPendingFilesForField,
    applyDatabasePrepopulation,
    customerId,
    setCustomerId,
    uploadPendingFiles,
    runSubmit,
    submitPhase,
    uploadProgress,
  };
};
